import { createContext, useRef, useEffect, ReactNode, useContext } from "react";

import { useAppSelector } from "@lib/stores";
import {
  changeLoading,
  changeVolume as changeVolumeState,
  pause as pauseState,
  play as playState,
  seek as seekState,
  changePlaybackRate as changePlaybackRateState,
  setDuration,
  togglePlayer,
  changeIsAutoStart,
  changePlayingState,
} from "@lib/stores/content";
import { getStorage } from "@modules/storage/storageHelper";

type OnPlay = () => void;
type OnPause = () => void;
type OnSeek = () => void;

interface AudioPlayerContextType {
  isShown: boolean;
  togglePlayer: (isShown?: boolean) => void;
  isPlaying: boolean | undefined;
  currentTime: number;
  duration: number;
  volume: number;
  playbackRate: number;
  isLoading: boolean;
  isAutoStart: boolean;
  play: (onPlay?: OnPlay) => void;
  pause: (onPause?: OnPause) => void;
  togglePlayPause: (onPlay?: () => void) => void;
  seek: (time: number, onSeek?: OnSeek) => void;
  changeVolume: (vol: number) => void;
  load: (url: string) => void;
  unload: () => void;
  changePlaybackRate: (playbackRate: number) => void;
  changeIsAutoStart: (isAutoStart: boolean) => void;
  changePlayingState: (state: boolean | undefined) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

interface AudioPlayerProviderProps {
  children: ReactNode;
  onStart: () => void;
  onEnd: () => void;
}

export const useAudioPlayer = (): AudioPlayerContextType => {
  const context = useContext(AudioPlayerContext);

  if (!context) {
    throw new Error("useAudioPlayer must be used within a AudioPlayerProvider");
  }
  return context;
};

export const AudioPlayerProvider = ({
  children,
  onStart,
  onEnd,
}: AudioPlayerProviderProps) => {
  const audioRef = useRef<HTMLAudioElement>(new Audio());

  const {
    isShown,
    isLoading,
    isPlaying,
    currentTime,
    volume,
    duration,
    playbackRate,
    isAutoStart,
  } = useAppSelector(({ content }) => content.player);

  const play = (onPlay?: OnPlay) => {
    // console.log("play");

    playState();

    onPlay && onPlay();
  };

  const pause = (onPause?: OnPause) => {
    // console.log("pause");

    pauseState();

    onPause && onPause();
  };

  const togglePlayPause = (onPlay?: OnPlay, onPause?: OnPause) => {
    if (isPlaying) {
      pause(onPause);
    } else {
      play(onPlay);
    }
  };

  const seek = (time: number, onSeek?: OnSeek) => {
    audioRef.current.currentTime = time;

    seekState(time);

    onSeek && onSeek();
  };

  const changeVolume = (vol: number) => {
    audioRef.current.volume = vol;

    changeVolumeState(vol);
  };

  const changePlaybackRate = (playbackRate: number) => {
    audioRef.current.playbackRate = playbackRate;

    changePlaybackRateState(playbackRate);
  };

  const load = (url: string) => {
    const audio = audioRef.current;

    audio.src = url;

    changeLoading(true);

    audioRef.current.load();

    audio.playbackRate = playbackRate;

    // console.log("loaded");

    pause();
    changePlayingState(undefined);
    seek(0);
    setDuration(0);

    audio.addEventListener("loadedmetadata", () => {
      setDuration(audio.duration);
      changeLoading(false);
    });

    onStart();
  };

  const unload = () => {
    const audio = audioRef.current;

    seek(0);

    audio.src = "";

    audio.load();

    // console.log("unloaded");

    pause();
    setDuration(0);
    changePlayingState(undefined);
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    timeout = setTimeout(() => {
      if (isPlaying) {
        audioRef.current
          .play()
          .then(() => {
            console.log("played");
          })
          .catch((error) => {
            console.log("playing failed");
          });
      } else {
        audioRef.current.pause();
      }
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [isPlaying]);

  useEffect(() => {
    (async () => {
      const player = await getStorage("PLAYER");

      togglePlayer(Boolean(player?.isShown));
    })();
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      changeLoading(false);
    };

    const handleTimeUpdate = () => {
      seekState(audio.currentTime);
    };

    const handleEnded = () => {
      pause();
      seek(0);
      setDuration(0);

      onEnd();
    };

    const handlePlay = () => {
      playState();
    };

    const handlePause = () => {
      pauseState();
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.pause();
    };
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{
        isShown,
        togglePlayer,
        isPlaying,
        currentTime,
        duration,
        volume,
        playbackRate,
        isLoading,
        isAutoStart,
        play,
        pause,
        togglePlayPause,
        seek,
        changeVolume,
        load,
        unload,
        changePlaybackRate,
        changeIsAutoStart,
        changePlayingState,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};
